import React from 'react';
import type { Tool, ToolGroup } from '../../types/developers';
import { InlineText } from './inline';
import { button, pageTitle } from './ui';

const nature = (tool: Tool) => {
  if (tool.annotations.readOnlyHint) return { label: '只读', className: 'bg-gray-100 text-gray-500' };
  if (tool.annotations.destructiveHint) return { label: '破坏性', className: 'bg-red-50 text-red-700' };
  return { label: '写入', className: 'bg-blue-50 text-blue-700' };
};

// /developers/tools: the registry, grouped server-side by McpToolGroups. Each
// row opens to the description the AI actually receives.
const ToolsTopic: React.FC<{ groups: ToolGroup[] }> = ({ groups }) => {
  const [open, setOpen] = React.useState<Record<string, boolean>>({});
  const all = groups.flatMap((g) => g.tools);
  const allOpen = all.length > 0 && all.every((t) => open[t.name]);

  const toggleAll = () => setOpen(allOpen ? {} : Object.fromEntries(all.map((t) => [t.name, true])));
  const toggle = (name: string) => setOpen((o) => ({ ...o, [name]: !o[name] }));

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h1 className={pageTitle}>能做什么</h1>
        <button type="button" className={button} onClick={toggleAll}>
          {allOpen ? '全部收起' : '全部展开'}
        </button>
      </div>
      <p className="mt-1.5 text-[15px] text-gray-700">{all.length} 个工具。点工具名看 AI 收到的原文。</p>

      <div className="mt-5 grid items-start gap-3 lg:grid-cols-3">
        {groups.map((group) => (
          <section key={group.key} className="rounded-xl border border-gray-200 bg-white px-3.5 pb-1.5 pt-3.5 shadow-sm">
            <h2 className="flex items-baseline gap-1.5 text-[15px] font-semibold text-gray-900">
              {group.name}
              <span className="text-xs font-normal text-gray-400">{group.tools.length}</span>
            </h2>
            {group.blurb && <p className="mb-2.5 mt-1.5 text-xs text-gray-700">{group.blurb}</p>}
            {group.tools.map((tool) => {
              const n = nature(tool);
              const isOpen = Boolean(open[tool.name]);
              return (
                <div key={tool.name} className="border-t border-gray-200">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => toggle(tool.name)}
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 py-1.5 text-left"
                  >
                    <code className={`truncate text-xs ${isOpen ? 'text-blue-600' : 'text-gray-900'}`}>{tool.name}</code>
                    <span className={`whitespace-nowrap rounded px-1.5 text-[11px] font-medium ${n.className}`}>{n.label}</span>
                    <span className="col-span-2 text-xs text-gray-500">{tool.title}</span>
                  </button>
                  {isOpen && (
                    <div className="mb-2 space-y-1.5 rounded-md bg-gray-50 px-2.5 py-2 text-xs text-gray-700">
                      <span className="block text-[10.5px] tracking-wide text-gray-400">AI 收到的说明</span>
                      {tool.paragraphs.map((p, i) => (
                        <p key={i}>
                          <InlineText text={p} />
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        ))}
      </div>

      <p className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
        <span className="rounded bg-gray-100 px-1.5 text-gray-500">只读</span>
        <span className="rounded bg-blue-50 px-1.5 text-blue-700">写入</span>
        <span className="rounded bg-red-50 px-1.5 text-red-700">破坏性</span>
        破坏性 = 客户端可能在执行前先问你一句。清单直接读服务端注册表。
      </p>
    </>
  );
};

export default ToolsTopic;
